import { DeviceController } from './device-controller';

export const SwiperController = {
  instances: new Map(),

  reset() {
    this.instances.forEach((instance) => instance.destroy(true, true));
    this.instances = new Map();
  },

  init() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('[data-swiper="true"]').forEach((el) => this.update(el));

    // DeviceController が PC↔SP を跨いだ時に発火する devicechange を購読
    window.addEventListener('devicechange', () => {
      document.querySelectorAll('[data-swiper="true"]').forEach((el) => this.update(el));
    });
  },

  // data-pc / data-sp の有無でデバイスごとの有効/無効を判定し生成・破棄
  // 有効な場合は既存インスタンスを破棄して現在のデバイス設定で再生成する
  update(el) {
    const isSP   = DeviceController.isSP;
    const hasPc  = el.hasAttribute('data-pc');
    const hasSp  = el.hasAttribute('data-sp');

    // どちらも指定なし → 常に有効（後方互換）
    const shouldActivate = (!hasPc && !hasSp) || (isSP ? hasSp : hasPc);

    const existing = this.instances.get(el);
    if (existing) {
      existing.destroy(true, true);
      this.instances.delete(el);
    }
    if (shouldActivate) {
      this.create(el);
    }
  },

  create(el) {
    const isSP    = DeviceController.isSP;
    const space   = isSP ? Number(el.dataset.space    ?? 16) : Number(el.dataset.spacePc  ?? 30);
    const rawSlides = isSP ? (el.dataset.slides ?? 1) : (el.dataset.slidesPc ?? 3);
    const slides  = rawSlides === 'auto' ? 'auto' : Number(rawSlides);
    const loop    = el.dataset.loop === 'true';
    const autoplay = el.dataset.autoplay;
    const centered = el.dataset.centered === 'true';
    const speed   = el.dataset.speed ? Number(el.dataset.speed) : undefined;

    const options = { loop, spaceBetween: space, slidesPerView: slides, centeredSlides: centered };
    if (speed !== undefined) options.speed = speed;

    // ★ observer 系: init 時にコンテナが display:none だった場合や
    //   親要素のサイズが後で確定するケース (SP↔PC 切替直後、モーダル内 swiper 等) で
    //   Swiper が自動的に update() を再実行するように監視を有効化。
    //   これを付けないと slide 幅が 0 or 巨大値 (例: 13,421,800px) になる不具合が発生する。
    options.observer = true;
    options.observeParents = true;
    options.observeSlideChildren = true;

    // ★ 切替エフェクト。data-effect="fade" でクロスフェードに切替
    // 対応: 'slide' (デフォルト) / 'fade'
    const effect = el.dataset.effect;
    if (effect === 'fade') {
      options.effect = 'fade';
      options.fadeEffect = { crossFade: true };
    }
    if (autoplay === 'marquee') {
      if (speed === undefined) options.speed = 4000;
      options.autoplay = { delay: 0, disableOnInteraction: false };
      options.cssEase = 'linear';
      // marquee は delay:0 の連続 transition 中は touch が効かないので
      // touchStart で現在位置に固定→autoplay 停止、touchEnd で再開する
      options.on = {
        touchStart(swiper) {
          swiper.setTransition(0);
          swiper.setTranslate(swiper.getTranslate());
          swiper.autoplay?.pause();
        },
        touchEnd(swiper) {
          swiper.autoplay?.resume();
        },
      };
    } else if (autoplay) {
      options.autoplay = { delay: Number(autoplay) };
    }

    // Swiper v11 の loop は `slideCount >= ceil(perView * 2) + 1` を満たさないと
    // クローン生成が破綻し、centeredSlides と組み合わさると右端に白抜けが発生する。
    // 枚数不足の時は loop を自動で無効化する。
    if (loop) {
      const slideCount = el.querySelectorAll('.swiper-slide').length;
      const perView    = slides === 'auto' ? 3 : slides;
      const minForLoop = Math.ceil(perView * 2) + 1;
      if (slideCount < minForLoop) {
        options.loop = false;
      } else if (el.dataset.loopAdditional) {
        options.loopAdditionalSlides = Number(el.dataset.loopAdditional);
      }
    }

    // .swiper-container の内側を優先、無ければ外側(document) から探す。
    // これで pagination/navigation/scrollbar をコンテナ外に置いても紐付けできる。
    const find = (sel) => {
      if (!sel) return null;
      return el.querySelector(sel) ?? document.querySelector(sel);
    };

    const paginationVal = isSP ? (el.dataset.paginationSp ?? el.dataset.pagination) : (el.dataset.paginationPc ?? el.dataset.pagination);
    const navigationVal = isSP ? (el.dataset.navigationSp ?? el.dataset.navigation) : (el.dataset.navigationPc ?? el.dataset.navigation);
    const scrollbarVal  = isSP ? (el.dataset.scrollbarSp  ?? el.dataset.scrollbar)  : (el.dataset.scrollbarPc  ?? el.dataset.scrollbar);
    if (paginationVal === 'true') {
      const paginationSel = el.dataset.paginationSel ?? '.swiper-pagination';
      // 外部要素対策で `el` は文字列セレクタで渡す。Swiper 側が document 全体から探すため
      // loop や動的差替でも失われず bullets を追随してくれる。
      options.pagination = {
        el: paginationSel,
        type: 'bullets',
        clickable: true,
        dynamicBullets: false,
      };
    }

    // ★ loop + 外部 pagination/scrollbar + マウスドラッグの組合せで
    // Swiper 内部の pagination.update() が現在の realIndex を拾えないことがあるため、
    // 手動で active bullet を切り替える安全網を追加する。
    // `slideChangeTransitionEnd` を使うと realIndex が確定した後に発火するため確実。
    const prevOn      = options.on ?? {};
    const paginationSelCache = ( paginationVal === 'true' )
      ? ( el.dataset.paginationSel ?? '.swiper-pagination' )
      : null;
    const scrollbarSelCache = ( scrollbarVal === 'true' )
      ? ( el.dataset.scrollbarSel ?? '.swiper-scrollbar' )
      : null;

    const syncIndicators = function ( swiper ) {
      // 手動 pagination 同期: realIndex に一致する bullet だけ active に
      if ( paginationSelCache ) {
        const pagEl = document.querySelector( paginationSelCache );
        if ( pagEl ) {
          const bullets = pagEl.querySelectorAll( '.swiper-pagination-bullet' );
          bullets.forEach( ( b, i ) => {
            b.classList.toggle( 'swiper-pagination-bullet-active', i === swiper.realIndex );
          } );
        }
      }
      // scrollbar 同期
      if ( scrollbarSelCache ) {
        swiper.scrollbar?.updateSize?.();
        swiper.scrollbar?.setTranslate?.();
      }
    };

    options.on = {
      ...prevOn,
      slideChange( swiper ) {
        prevOn.slideChange?.call( this, swiper );
        syncIndicators( swiper );
      },
      slideChangeTransitionEnd( swiper ) {
        prevOn.slideChangeTransitionEnd?.call( this, swiper );
        // realIndex 確定後の最終同期
        syncIndicators( swiper );
      },
    };
    if (navigationVal === 'true') {
      // デバイス別にセレクタを指定可能:
      //   data-nav-next-pc / data-nav-prev-pc  → PC 時に使用
      //   data-nav-next-sp / data-nav-prev-sp  → SP 時に使用
      //   data-nav-next    / data-nav-prev     → 共通フォールバック
      // 同じページに複数 swiper があり、かつ PC/SP でボタン要素を分けている場合は
      // ここでデバイスごとに固有クラスを割り当てることで誤バインドを防げる。
      const nextSel = isSP
        ? (el.dataset.navNextSp ?? el.dataset.navNext ?? '.swiper-button-next')
        : (el.dataset.navNextPc ?? el.dataset.navNext ?? '.swiper-button-next');
      const prevSel = isSP
        ? (el.dataset.navPrevSp ?? el.dataset.navPrev ?? '.swiper-button-prev')
        : (el.dataset.navPrevPc ?? el.dataset.navPrev ?? '.swiper-button-prev');
      // find() で「まず container 内 → 無ければ document 全体」の順で解決。
      // 外部要素に置いても紐付く。見つからなければセレクタ文字列のまま Swiper に委ねる。
      options.navigation = {
        nextEl: find(nextSel) ?? nextSel,
        prevEl: find(prevSel) ?? prevSel,
      };
    }
    if (scrollbarVal === 'true') {
      const scrollbarSel = el.dataset.scrollbarSel ?? '.swiper-scrollbar';
      // 外部要素対策: DOMノードではなくセレクタ文字列で渡すことで、
      // Swiper 側が document 全体から解決 → 双方向の同期が安定する。
      // hide:false で常時表示 (デフォルトは操作後フェードアウト)。
      // snapOnRelease:true でドラッグ離した時に最寄のスライドにスナップ。
      options.scrollbar = {
        el: scrollbarSel,
        draggable: true,
        hide: false,
        snapOnRelease: true,
      };
    }

    const instance = new Swiper(el, options);

    if (autoplay === 'marquee') {
      el.style.setProperty('--swiper-wrapper-transition-timing-function', 'linear');
    }

    // autoplay は「可視範囲に入ってから 3 秒後」に開始する
    // ただし marquee は演出上、ページアクセス直後から流し続ける
    if (autoplay && autoplay !== 'marquee' && instance.autoplay) {
      instance.autoplay.stop();
      let timerId;
      const io = new IntersectionObserver(([entry], obs) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        timerId = setTimeout(() => instance.autoplay?.start(), 3000);
      });
      io.observe(el);
      instance.on('beforeDestroy', () => {
        io.disconnect();
        if (timerId) clearTimeout(timerId);
      });
    }

    this.instances.set(el, instance);
    return instance;
  },
};
